import React, { useContext } from 'react';
import {
  Close,
  Email,
  Phone,
  Person,
  Business,
  LocalOffer,
  Payment,
  PersonPin,
  LocationOn,
  Category,
  Public,
  Info
} from '@mui/icons-material';
import { Typography, Chip } from '@mui/material';
import DrawerContext from '../../../context/DrawerContext';
import "../../../styles/Drawer.scss";
import "../../../Pages/CouponLeads/CouponLeads.scss";

const ViewPopinLeadDrawer = ({ drawerDetails }) => {
  const drawerCtx = useContext(DrawerContext);

  // Supports:
  // - entity object (old)
  // - { entity: {...} }
  // - { data: { entity: {...} } } (new API)
  const popin = drawerDetails?.data?.entity || drawerDetails?.entity || drawerDetails || {};
  const coupon = popin?.coupon || null;
  const advertising = popin?.advertising || coupon?.advertising || {};
  // New API returns locations at entity level: data.entity.locations
  const locations = popin?.locations || coupon?.locations || [];
  const categories = popin?.categories || coupon?.categories || [];
  const countries = popin?.countries || coupon?.countries || [];

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatDeal = (couponDetails) => {
    const deal = couponDetails?.deal;
    const dealItem = couponDetails?.deal_item;
    const dealUnit = (couponDetails?.deal_unit || '').toUpperCase();
    if (!deal) return null;
    if (dealUnit === 'PER') return `${deal}% Off`;
    if (dealUnit === 'FREE') {
      const parts = [deal, dealItem, dealUnit].filter(Boolean);
      return parts.join(' ');
    }
    return `${deal} ${dealUnit} Off`;
  };

  const pipeToList = (str) => {
    if (!str || typeof str !== 'string') return [];
    return str.split('|').map((s) => s.trim()).filter(Boolean);
  };

  const renderStatusBadge = (status, type = 'status') => {
    if (type === 'status' && status === 'draft') {
      return <div className="status-draft">Draft</div>;
    }

    if (type === 'payment_status') {
      const paymentStatus = status;

      if (paymentStatus === 'completed') return <div className="status-active">Paid</div>;
      if (paymentStatus === 'pending') return <div className="status-pending">Pending</div>;
      if (paymentStatus === 'failed') return <div className="status-deleted">Failed</div>;
      if (paymentStatus === 'cancelled') return <div className="status-suspended">Cancelled</div>;

      return paymentStatus ? <div className="status-unknown">{paymentStatus}</div> : null;
    }

    return null;
  };

  const InfoRow = ({ icon: Icon, label, value, link = null, isBadge = false, badgeComponent = null, hideIfEmpty = false, asList = false }) => {
    if (hideIfEmpty && (!value || value === 'N/A')) return null;

    if (asList) {
      const items = pipeToList(value);
      if (hideIfEmpty && items.length === 0) return null;
      return (
        <div className="info-row">
          <div className="info-row-content info-row-content-list">
            {Icon && <Icon className="info-icon" />}
            <Typography variant="body2" className="info-label">
              {label}:
            </Typography>
            <div className="info-value">
              {items.length > 0 ? (
                <ul className="info-list">
                  {items.map((item, i) => (
                    <li key={i}>
                      <Typography variant="body2" className="info-text">
                        {item}
                      </Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2" className="info-placeholder">
                  Not provided
                </Typography>
              )}
            </div>
          </div>
        </div>
      );
    }

    const displayValue = value && value !== 'N/A' ? String(value) : null;
    const isBold = label.includes('ID') || label.includes('Cost') || label.includes('Code');

    return (
      <div className="info-row">
        <div className="info-row-content">
          {Icon && <Icon className="info-icon" />}
          <Typography variant="body2" className="info-label">
            {label}:
          </Typography>
          <div className="info-value">
            {isBadge && badgeComponent ? (
              badgeComponent
            ) : displayValue ? (
              link ? (
                <a href={link} target="_blank" rel="noopener noreferrer" className="info-link">
                  {displayValue}
                </a>
              ) : (
                <Typography variant="body2" className={`info-text ${isBold ? 'bold' : ''}`}>
                  {displayValue}
                </Typography>
              )
            ) : (
              <Typography variant="body2" className="info-placeholder">
                Not provided
              </Typography>
            )}
          </div>
        </div>
      </div>
    );
  };

  const SectionHeader = ({ icon: Icon, title, count = null }) => (
    <div className="section-header">
      {Icon && <Icon className="section-icon" />}
      <Typography variant="h6" className="section-title">
        {title} {count !== null && `(${count})`}
      </Typography>
    </div>
  );

  const isPopinWithCoupon = !!popin?.coupon_id || !!coupon?.id;

  return (
    <div className="coupon-lead-drawer">
      <div className="details-section">
        <header>
          <h1>{isPopinWithCoupon ? 'Pop-in (with Coupon) Details' : 'Pop-in Details'}</h1>
          <Close onClick={() => drawerCtx.closeDrawer()} className="close-icon" />
        </header>

        <section>
          <div className="details drawer-section">
            <SectionHeader icon={Business} title="Company Information" />
            <div className="card drawer-card">
              <div className="row">
                <div className="company-header">
                  <div
                    className="company-logo-wrap"
                    style={advertising.background_color ? { backgroundColor: advertising.background_color } : undefined}
                  >
                    {advertising.logo_md || advertising.logo_sm ? (
                      <img
                        src={advertising.logo_md || advertising.logo_sm}
                        alt="Company Logo"
                        className="company-logo"
                      />
                    ) : (
                      <div className="company-logo-placeholder">
                        {(advertising.companyName || advertising.company_name)
                          ? (advertising.companyName || advertising.company_name).charAt(0).toUpperCase()
                          : 'N'}
                      </div>
                    )}
                  </div>
                  <Typography variant="h6" className="company-name">
                    {advertising.companyName || advertising.company_name || popin.company_name || 'N/A'}
                  </Typography>
                </div>
              </div>

              <InfoRow
                icon={Email}
                label="Email"
                value={advertising.email_address || popin.email_address || popin.customer_email}
                link={(advertising.email_address || popin.email_address || popin.customer_email) ? `mailto:${advertising.email_address || popin.email_address || popin.customer_email}` : null}
              />
              <InfoRow
                icon={Phone}
                label="Phone"
                value={advertising.phone_number || popin.phone_number}
                link={(advertising.phone_number || popin.phone_number) ? `tel:${advertising.phone_number || popin.phone_number}` : null}
              />
              <InfoRow
                icon={Person}
                label="Contact Person"
                value={advertising.contact_person || popin.contact_person}
              />
            </div>
          </div>

          <div className="details drawer-section">
            <SectionHeader icon={Payment} title="Payment & Status" />
            <div className="card drawer-card">
              <InfoRow
                label="Status"
                value={popin.status}
                isBadge={true}
                badgeComponent={renderStatusBadge(popin.status, 'status') || (popin.payment_status ? renderStatusBadge(popin.payment_status, 'payment_status') : null)}
              />
              <InfoRow label="Affiliate Code" value={popin.affiliate_code} hideIfEmpty={true} />
              <InfoRow
                label="Payment Status"
                value={popin.payment_status}
                isBadge={true}
                badgeComponent={renderStatusBadge(popin.payment_status, 'payment_status')}
                hideIfEmpty={true}
              />
              <InfoRow label="Claims Remaining" value={popin.claims_remaining} hideIfEmpty={true} />
              <InfoRow label="Price" value={popin.price} hideIfEmpty={true} />
            </div>
          </div>

          {/* Coupon Details (if linked) */}
          {coupon && (
            <div className="details drawer-section">
              <SectionHeader icon={LocalOffer} title="Coupon Details" />
              <div className="card drawer-card">
                <InfoRow label="Coupon ID" value={coupon.id || popin.coupon_id} hideIfEmpty={true} />
                <InfoRow label="Coupon Code" value={coupon.coupon} hideIfEmpty={true} />
                <InfoRow label="Deal" value={formatDeal(coupon)} hideIfEmpty={true} />
                <InfoRow label="Description" value={coupon.description} hideIfEmpty={true} />
                <InfoRow label="Terms" value={coupon.terms} hideIfEmpty={true} asList={true} />
                <InfoRow label="Instructions" value={coupon.instructions} hideIfEmpty={true} asList={true} />
                <InfoRow
                  label="Online Only"
                  value={coupon.online_only === 'yes' ? 'Yes' : coupon.online_only === 'no' ? 'No' : null}
                  hideIfEmpty={true}
                />
                {coupon.website && (
                  <InfoRow
                    label="Website"
                    value={coupon.website}
                    link={coupon.website}
                  />
                )}
              </div>
            </div>
          )}

          {/* Customer Information */}
          <div className="details drawer-section">
            <SectionHeader icon={PersonPin} title="Customer Information" />
            <div className="card drawer-card">
              <InfoRow
                icon={Person}
                label="Name"
                value={popin.customer_name}
                hideIfEmpty={true}
              />
              <InfoRow
                icon={Email}
                label="Email"
                value={popin.customer_email}
                link={popin.customer_email ? `mailto:${popin.customer_email}` : null}
                hideIfEmpty={true}
              />
            </div>
          </div>

          {/* Agent Information */}
          <div className="details drawer-section">
            <SectionHeader icon={Person} title="Agent Information" />
            <div className="card drawer-card">
              {popin.affiliate ? (
                <>
                  <InfoRow label="Agent Name" value={popin.affiliate.name} hideIfEmpty={true} />
                  <InfoRow label="Affiliate Code" value={popin.affiliate.code} hideIfEmpty={true} />
                  {popin.affiliate.agent_fee != null && (
                    <InfoRow
                      label="Points awarded"
                      value={`${parseFloat(popin.affiliate.agent_fee).toFixed(2)} points`}
                    />
                  )}
                </>
              ) : (
                <InfoRow label="Agent" value="No agent associated with this pop-in." />
              )}
            </div>
          </div>

          {/* Locations / Categories / Countries (from coupon) */}
          {locations.length > 0 && (
            <div className="details drawer-section">
              <SectionHeader icon={LocationOn} title="Locations" count={locations.length} />
              <div className="card drawer-card">
                {locations.map((location, index) => {
                  const requiresManualSync = location.manualSync === "yes";
                  return (
                    <div
                      key={location.id || index}
                      className={`${index < locations.length - 1 ? "location-item" : "location-item"} ${requiresManualSync ? "manual-sync-disabled" : ""}`}
                      style={{
                        ...(index === locations.length - 1 ? { borderBottom: 'none', paddingBottom: 0, marginBottom: 0 } : {}),
                        opacity: requiresManualSync ? 0.5 : 1,
                        backgroundColor: requiresManualSync ? "#f5f5f5" : "transparent"
                      }}
                    >
                      <Typography variant="body2" className="location-title">
                        {location.title || 'N/A'}
                      </Typography>
                      <Typography variant="body2" className="location-address">
                        {location.address || 'N/A'}
                      </Typography>
                      {location.latitude && location.longitude && (
                        <Typography variant="body2" className="location-coords">
                          {location.latitude}, {location.longitude}
                        </Typography>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {categories.length > 0 && (
            <div className="details drawer-section">
              <SectionHeader icon={Category} title="Categories" count={categories.length} />
              <div className="card drawer-card">
                <div className="chip-container">
                  {categories.map((category, index) => (
                    <Chip
                      key={category.id || index}
                      label={`${category.title || 'N/A'} ${category.parent > 0 ? '(Sub)' : '(Main)'}`}
                      size="small"
                      sx={{
                        backgroundColor: category.parent > 0 ? '#e3f2fd' : '#f3e5f5',
                        color: category.parent > 0 ? '#1976d2' : '#7b1fa2',
                        fontWeight: '500',
                        fontSize: '12px'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {countries.length > 0 && (
            <div className="details drawer-section">
              <SectionHeader icon={Public} title="Countries" count={countries.length} />
              <div className="card drawer-card">
                <div className="chip-container">
                  {countries.map((country, index) => (
                    <Chip
                      key={country.id || index}
                      label={`${country.name || 'N/A'} (${country.iso || 'N/A'})`}
                      size="small"
                      sx={{
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        fontWeight: '500',
                        fontSize: '12px'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="details drawer-section">
            <SectionHeader icon={Info} title="Additional Information" />
            <div className="card drawer-card">
              <InfoRow label="Created At" value={formatDate(popin.created_at)} />
              <InfoRow label="Updated At" value={formatDate(popin.updated_at)} hideIfEmpty={true} />
              <InfoRow label="Pop-in ID" value={popin.id} hideIfEmpty={true} />
              <InfoRow label="Advertising ID" value={popin.advertising_id} hideIfEmpty={true} />
              <InfoRow label="Coupon ID" value={popin.coupon_id} hideIfEmpty={true} />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ViewPopinLeadDrawer;

